import { createStyles, makeStyles } from "@material-ui/core";
import { AutocompleteInputChangeReason } from "@material-ui/lab";
import classnames from "classnames";
import * as React from "react";

import { RelodeTheme, SelectInput, useDebounce, useRelodeApp } from "..";
import { ParentTeamSuggestion } from "./types";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {},
            downshift: {
                margin: theme.spacing(0, 0, 4, 0),
            },
            menuEmptyMessage: {
                height: "auto",
                whiteSpace: "initial",
            },
            paper: {
                overflow: "visible",
            },
            menuItemDetails: {
                whiteSpace: "normal",
                height: "auto",
                display: "block",
            },
        }),
    { name: "teamSuggestionInput" }
);

interface ParentTeamSuggestProps {
    className?: string;
    id: string;
    label: string;
    currentTeamId?: number;
    onTeamSelection: (team?: ParentTeamSuggestion) => void;
    selectedTeam: ParentTeamSuggestion;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
}

export const ParentTeamSuggest = (props: ParentTeamSuggestProps) => {
    const {
        id,
        label,
        currentTeamId,
        onTeamSelection,
        className,
        disabled,
        selectedTeam,
    } = props;
    const classes = useStyles({});
    const rootClasses = classnames(classes.root, className);
    const {
        state: {
            config: { parentTeamLookupService },
        },
    } = useRelodeApp();

    const [teamSuggestions, setTeamSuggestions] = React.useState<
        ParentTeamSuggestion[]
    >();

    const loadTeamSuggestion = useDebounce(
        async (searchText: string) => {
            setTeamSuggestions(
                await parentTeamLookupService.loadSuggestions(
                    searchText,
                    currentTeamId
                )
            );
        },
        500,
        []
    );

    const handleLoadSuggestion = async (
        event: never,
        value: string,
        reason: AutocompleteInputChangeReason
    ) => {
        if (!value || value.trim() === "" || value.trim().length === 0) {
            setTeamSuggestions([]);
            onTeamSelection(undefined);
            return;
        }

        await loadTeamSuggestion(value);
    };

    const handleTeamChange = (e: never, newValue: ParentTeamSuggestion) => {
        onTeamSelection(newValue);
    };

    const getOptionLabel = (option: ParentTeamSuggestion) =>
        `${option?.teamName ?? ""}`;

    return (
        <div className={rootClasses}>
            <SelectInput
                mode="suggest"
                clearable
                disabled={disabled}
                error={props.error}
                getOptionLabel={getOptionLabel}
                helperText={props.helperText}
                id={id}
                label={label}
                noOptionsText="Your keywords did not match any team."
                onChange={handleTeamChange}
                onInputChange={handleLoadSuggestion}
                options={teamSuggestions ?? []}
                value={selectedTeam}
            />
        </div>
    );
};
